import { Link, useNavigate } from "react-router-dom"
import { motion } from "framer-motion"
import { Crown, Shield, Zap, ArrowRight, Calendar, BarChart3, Users, Trophy, Star } from "lucide-react"
import { useLatestLotteryResult, useActiveAnnouncements, useAuth, useClientConfig } from '@workspace/public-utils'
import { Seo } from "../components/Seo"

// LotteryBall component for displaying numbers with Korean lottery colors
const LotteryBall = ({ number, isBonus = false }: { number: number; isBonus?: boolean }) => {
  const getNumberStyle = (num: number) => {
    if (isBonus) return "lottery-number-bonus"
    if (num <= 5) return "lottery-number-1-5"
    if (num <= 10) return "lottery-number-6-10"
    if (num <= 15) return "lottery-number-11-15"
    if (num <= 20) return "lottery-number-16-20"
    if (num <= 25) return "lottery-number-21-25"
    if (num <= 30) return "lottery-number-26-30"
    if (num <= 35) return "lottery-number-31-35"
    if (num <= 40) return "lottery-number-36-40"
    return "lottery-number-41-45"
  }

  return (
    <motion.div
      className={`lottery-number ${getNumberStyle(number)} w-14 h-14 md:w-16 md:h-16 rounded-full flex items-center justify-center text-white font-bold text-lg md:text-xl shadow-lg`}
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      transition={{ duration: 0.5, delay: (number % 6) * 0.1 }}
      whileHover={{ scale: 1.1 }}
    >
      {number}
    </motion.div>
  )
}

// Feature card component
const FeatureCard = ({ icon: Icon, title, description, highlight = false }: {
  icon: any
  title: string
  description: string
  highlight?: boolean
}) => (
  <motion.div
    className={`bg-white rounded-2xl p-6 text-center shadow-lg hover:shadow-xl transition-all duration-300 ${highlight ? 'ring-2 ring-happiness-primary shadow-2xl transform scale-105' : ''}`}
    whileHover={{ y: -8, scale: highlight ? 1.05 : 1.02 }}
    transition={{ duration: 0.3 }}
  >
    <div className={`mx-auto w-16 h-16 ${highlight ? 'bg-gradient-to-br from-happiness-primary to-purple-600' : 'bg-gradient-to-br from-gray-100 to-gray-200'} rounded-full flex items-center justify-center mb-4`}>
      <Icon className={`h-8 w-8 ${highlight ? 'text-white' : 'text-happiness-primary'}`} />
    </div>
    <h3 className="text-xl font-bold text-gray-900 mb-3">{title}</h3>
    <p className="text-gray-600 leading-relaxed">{description}</p>
  </motion.div>
)

// Statistics card component
const StatCard = ({ icon: Icon, title, value, description }: {
  icon: any
  title: string
  value: string
  description: string
}) => (
  <motion.div
    className="bg-white rounded-2xl p-6 text-center shadow-lg hover:shadow-xl transition-all duration-300"
    whileHover={{ y: -5, scale: 1.02 }}
    transition={{ duration: 0.2 }}
  >
    <div className="mx-auto w-12 h-12 bg-gradient-to-br from-happiness-primary to-purple-600 rounded-lg flex items-center justify-center mb-3">
      <Icon className="h-6 w-6 text-white" />
    </div>
    <h3 className="text-lg font-bold text-happiness-primary mb-1">{title}</h3>
    <p className="text-3xl font-bold bg-gradient-to-r from-happiness-primary to-purple-600 bg-clip-text text-transparent mb-1">{value}</p>
    <p className="text-sm text-gray-600">{description}</p>
  </motion.div>
)

const HomePage = () => {
  const config = useClientConfig()
  const { data: latestResult, isLoading: resultLoading } = useLatestLotteryResult()
  const { data: announcements } = useActiveAnnouncements()
  const { isAuthenticated } = useAuth()
  const navigate = useNavigate()

  const handleJoinNowClick = () => {
    if (isAuthenticated) {
      navigate('/plans')
    } else {
      navigate('/auth/register')
    }
  }

  return (
    <div className="min-h-screen">
      <Seo
        title={`${config.brandName} - ${config.seoTitleSuffix}`}
        description="프리미엄 로또 번호 생성 및 분석 서비스 - 최신 당첨번호 조회와 통계 분석으로 더 스마트하게 로또를 즐기세요."
        canonicalPath="/"
      />
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-happiness-primary via-purple-600 to-indigo-700 py-20 px-4">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="max-w-7xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl md:text-7xl font-black text-white mb-6 drop-shadow-lg">
              {config.brandName}
            </h1>
            <h2 className="text-2xl md:text-3xl font-bold text-white/90 mb-4">
              {config.tagline}
            </h2>
            <p className="text-lg md:text-xl text-white/80 mb-8 max-w-3xl mx-auto">
              {config.subTagline}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              {!isAuthenticated && (
                <Link to="/auth/register" className="bg-white text-happiness-primary font-bold py-4 px-8 rounded-full hover:bg-gray-100 transition-colors duration-200 flex items-center text-lg shadow-lg">
                  지금 시작하기
                  <ArrowRight className="ml-2 h-6 w-6" />
                </Link>
              )}
              <Link to="/results" className="border-2 border-white text-white font-bold py-4 px-8 rounded-full hover:bg-white hover:text-happiness-primary transition-colors duration-200 flex items-center text-lg">
                최신 결과 보기
                <Calendar className="ml-2 h-5 w-5" />
              </Link>
            </div>
          </motion.div>
        </div>

        {/* Floating elements */}
        <div className="absolute top-10 left-10 w-20 h-20 bg-white/10 rounded-full animate-pulse"></div>
        <div className="absolute top-32 right-20 w-16 h-16 bg-white/10 rounded-full animate-pulse delay-300"></div>
        <div className="absolute bottom-20 left-1/4 w-12 h-12 bg-white/10 rounded-full animate-pulse delay-700"></div>
      </section>

      {/* Latest Results Section */}
      {!resultLoading && latestResult && (
        <section className="py-16 px-4 bg-gray-50">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl font-bold bg-gradient-to-r from-happiness-primary to-purple-600 bg-clip-text text-transparent mb-4">
                최신 당첨 번호
              </h2>
              <p className="text-xl text-gray-600">
                제 {latestResult.round_number}회 당첨 결과
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-white rounded-3xl p-8 max-w-4xl mx-auto shadow-xl border border-gray-100"
            >
              <div className="flex flex-wrap justify-center items-center gap-4 mb-6">
                {(latestResult.numbers || []).map((num: number, idx: number) => (
                  <LotteryBall key={idx} number={num} />
                ))}
                <div className="mx-4 text-3xl text-gray-400 font-bold">+</div>
                <LotteryBall number={latestResult.bonus_number} isBonus={true} />
              </div>
              
              <div className="text-center border-t border-gray-100 pt-6">
                <p className="text-lg text-gray-600 mb-4">
                  추첨일: {new Date(latestResult.draw_date).toLocaleDateString('ko-KR')}
                </p>
                <Link 
                  to="/results" 
                  className="bg-gradient-to-r from-happiness-primary to-purple-600 text-white font-bold py-3 px-8 rounded-full hover:shadow-lg transition-all duration-200 inline-flex items-center"
                >
                  전체 결과 보기
                  <BarChart3 className="ml-2 h-5 w-5" />
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Features Section */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold bg-gradient-to-r from-happiness-primary to-purple-600 bg-clip-text text-transparent mb-4">
              {config.whyChooseUs}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              대한민국 최고의 온라인 복권 서비스로 안전하고 투명한 복권 구매 경험을 제공합니다
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <FeatureCard
              icon={Shield}
              title="100% 안전 보장"
              description="정부 인증 복권 판매점으로 모든 거래가 안전하게 보호됩니다"
              highlight={true}
            />
            <FeatureCard
              icon={Zap}
              title="실시간 결과 확인"
              description="추첨 즉시 결과를 확인하고 당첨 여부를 바로 알 수 있습니다"
            />
            <FeatureCard
              icon={Crown}
              title="VIP 혜택"
              description="정기 구독 회원에게는 특별한 혜택과 할인을 제공합니다"
            />
          </div>
        </div>
      </section>

      {/* Prob-X Analysis Section */}
      <section className="py-20 px-4 bg-gray-50 relative overflow-hidden" data-testid="probx-section">
        <div className="max-w-2xl mx-auto text-center">
          {/* Main Title */}
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-3xl md:text-5xl font-black text-gray-800 leading-tight mb-4"
          >
            당첨되지 않은 이유를 분석할 때,<br />비로소 확률은 움직인다
          </motion.h1>

          {/* Subtitle */}
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mt-4 text-base md:text-xl text-gray-600"
          >
            AI 확률 분석 시스템, <span className="font-bold text-gray-800">PRO-BX.</span> 데이터로 읽는 행운의 공식
          </motion.p>

          {/* Action Buttons */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-10 flex flex-col sm:flex-row gap-4 justify-center"
          >
            <button 
              onClick={handleJoinNowClick}
              className="w-full sm:w-auto bg-yellow-400 hover:bg-yellow-500 text-gray-900 font-bold py-3 px-8 rounded-full shadow-lg transform hover:scale-105 transition-transform duration-300 ease-in-out"
            >
              즉시 참여하기
            </button>
            <Link 
              to="/plans"
              className="w-full sm:w-auto bg-white hover:bg-gray-100 text-gray-800 font-bold py-3 px-8 rounded-full border border-gray-300 shadow-lg transform hover:scale-105 transition-transform duration-300 ease-in-out"
            >
              더 알아보기
            </Link>
          </motion.div>

          {/* Puzzle Pieces Section */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-16 relative h-80 md:h-[28rem] w-full max-w-2xl mx-auto scale-100"
          >
            {/* Green Piece - Mathematical Analysis */}
            <motion.div 
              whileHover={{ y: -5, scale: 1.05 }}
              className="puzzle-piece bg-green-500 w-56 h-36 md:w-64 md:h-40 absolute bottom-10 left-0 md:left-6 transform -rotate-12 flex items-center justify-center text-white font-bold text-2xl md:text-3xl rounded-2xl shadow-lg"
              data-testid="probx-piece-math"
            >
              <span>수학적 분석</span>
            </motion.div>
            
            {/* Yellow Piece - Theoretical Tracking */}
            <motion.div 
              whileHover={{ y: -5, scale: 1.05 }}
              className="puzzle-piece bg-yellow-400 w-56 h-36 md:w-64 md:h-40 absolute top-12 left-10 md:left-16 transform -rotate-6 flex items-center justify-center text-gray-900 font-bold text-2xl md:text-3xl rounded-2xl shadow-lg"
              data-testid="probx-piece-theory"
            >
              <span>이론적 추적</span>
            </motion.div>

            {/* Orange Piece - Combination Generation */}
            <motion.div 
              whileHover={{ y: -5, scale: 1.05 }}
              className="puzzle-piece bg-orange-500 w-52 h-32 md:w-60 md:h-36 absolute bottom-8 right-0 md:right-6 transform rotate-12 flex items-center justify-center text-white font-bold text-xl md:text-2xl rounded-2xl shadow-lg"
              data-testid="probx-piece-combination"
            >
              <span>조합 생성</span>
            </motion.div>

            {/* Pink Piece - PRO-BX Logo */}
            <motion.div 
              whileHover={{ y: -5, scale: 1.05 }}
              className="puzzle-piece bg-pink-500 w-64 h-40 md:w-72 md:h-44 absolute top-0 right-6 md:right-10 transform rotate-6 flex items-center justify-center text-white font-black text-4xl md:text-5xl rounded-2xl shadow-lg"
              data-testid="probx-piece-logo"
            >
              <span>PRO-BX</span>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Statistics Section */}
      <section className="py-16 px-4 bg-gradient-to-br from-gray-50 to-purple-50">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold bg-gradient-to-r from-happiness-primary to-purple-600 bg-clip-text text-transparent mb-4">
              믿을 수 있는 숫자들
            </h2>
            <p className="text-xl text-gray-600">
              {config.statsSubtitle}
            </p>
          </motion.div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            <StatCard
              icon={Users}
              title="총 회원수"
              value="50만+"
              description="믿고 이용하는 회원들"
            />
            <StatCard
              icon={Trophy}
              title="총 당첨액"
              value="1,250억원"
              description="지금까지 지급된 상금"
            />
            <StatCard
              icon={Star}
              title="만족도"
              value="98.5%"
              description="고객 만족도 평가"
            />
            <StatCard
              icon={Crown}
              title="1등 당첨자"
              value="127명"
              description="꿈을 이룬 당첨자들"
            />
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      {!isAuthenticated && (
        <section className="py-20 px-4 bg-gradient-to-br from-happiness-primary via-purple-600 to-indigo-700 relative overflow-hidden">
          <div className="absolute inset-0 bg-black/10"></div>
          <div className="max-w-4xl mx-auto text-center relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 drop-shadow-lg">
                당신의 행운이 기다리고 있습니다
              </h2>
              <p className="text-xl md:text-2xl text-white/90 mb-8">
                {config.ctaJoinText}
              </p>
              <Link to="/auth/register" className="bg-white text-happiness-primary font-bold py-4 px-12 rounded-full hover:bg-gray-100 transition-colors duration-200 inline-flex items-center text-xl shadow-lg">
                회원가입 하기
                <ArrowRight className="ml-2 h-6 w-6" />
              </Link>
            </motion.div>
          </div>
        </section>
      )}

      {/* Announcements Section */}
      {announcements && announcements.length > 0 && (
        <section className="py-12 px-4 bg-purple-50/50">
          <div className="max-w-7xl mx-auto">
            <h3 className="text-2xl font-bold bg-gradient-to-r from-happiness-primary to-purple-600 bg-clip-text text-transparent mb-6 text-center">
              공지사항
            </h3>
            <div className="grid gap-4">
              {announcements.slice(0, 3).map((announcement: any) => (
                <motion.div
                  key={announcement.id}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  className="bg-white rounded-2xl p-6 hover:shadow-lg transition-shadow cursor-pointer border border-gray-100"
                >
                  <div className="flex items-center justify-between">
                    <h4 className="text-lg font-semibold text-gray-900">
                      {announcement.title}
                    </h4>
                    <span className="text-sm text-gray-500">
                      {new Date(announcement.created_at).toLocaleDateString('ko-KR')}
                    </span>
                  </div>
                  <p className="text-gray-600 mt-2 line-clamp-2">
                    {announcement.content}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  )
}

export default HomePage
